import {PowerOfAppointment} from "../components/PowersOfAppointmentSection/PowerOfAppointment";
import {EstateItemType} from "../forms/EstateItemType";
import {IrrevocableTrust} from "./IrrevocableTrust";
import {FlowchartCustomEdgeProps} from "../components/FlowchartVisual/Edges/FlowchartCustomEdge";
import {Will} from "./Will";
import {DistributionsNodeProps} from "../components/FlowchartVisual/Nodes/DistributionsNode";
import {WillSummary} from "./WillSummary";
import {EstateCardViewModeType, EstateTaxDueType} from "../components/EstateCard/EstateCard";
import {FirstLevelParentNodeProps} from "../components/FlowchartVisual/Nodes/FirstLevelParentNode";
import {Edge, Node} from "@xyflow/react";
import {Miscellaneous} from "./Miscellaneous";

/**
 *There can be more than 1 flowchart per Profile; Each flowchart will have at most revTrust and 0-many irrevTrust
 */
export type EstateFlowchartState = {
    flowchartId: string | undefined,
    flowchartName?: string,
    revocableTrust?: RevocableTrust | null,
    irrevocableTrusts?: IrrevocableTrust[]
    wildcardTrusts?: Miscellaneous[],
    willSummary?: WillSummary,
    viewMode?: EstateCardViewModeType,
    estateToggle?: EstateToggle,
    estateTaxDueTypes?: EstateTaxDueType[]
}

export enum ModalSource {
    "AddFlowchart",
    "AddEstateItem",
    "DuplicateFlowchart"
}

export enum EstateToggle {
    IN_ESTATE = "In Estate",
    FULL_ESTATE = "Full Estate",
    OUT_OF_ESTATE = "Out of Estate"
}

export type EstateItemTypes = RevocableTrust | IrrevocableTrust | Will | Miscellaneous;

export type EstateItemObject = {
    type: EstateItemType,
    id: string,
    nickName: string
}

export interface EstateItem {
    trustId: string;
}

export interface RevocableTrust extends EstateItem {
    flowchartName: string,
    legalName: string,
    nickName: string,
    trustCreationDate: string,
    recordOfAmendment: string | null
    grantors: Trustee[],
    trustees: Trustee[],
    successors: Trustee[],
    funding: string | null,
    investmentAdvisor: string | null,
    trustProtector: string | null,
    trustJurisdiction?: string,
    assumedFirstDeath: string,
    hasBeneficiariesAtFirstDeath: boolean,
    beneficiaries?: Beneficiaries | null,
}

export type TriggerEventType = "" | "DEATH" | "AGE_ATTAINMENT" | "OTHER";

export interface AdditionalDistributionLayer {
    triggerEventType: TriggerEventType,
    triggerMemberId: string,
    triggerAge?: number | null,
    triggerEventText?: string,
    triggerMemberName?: string,
    distributions?: (Trust | Charity | Outright)[]
}

export type Beneficiaries = {
    firstDeathDistributions: (Trust | Charity | Outright)[],
    remainderDistributions: (Trust | Charity | Outright)[],
    additionalDistributions?: AdditionalDistributionLayer
}

export interface Distribution {
    name?: string,
    distributionType: DistributionType,
    distributionId?: string,
    beneficiaryId?: string,
    funding?: string,
    sequenceNumber?: number,
    createdDate?: string
}

export type Trust = Distribution & {
    tags: string,
    trustees: Trustee[],
    successors: Trustee[],
    beneficiaries: Trustee[],
    distributions: string,
    termination: string,
    powersOfAppointment: PowerOfAppointment[],
}

export type NonTrustDistribution = Distribution & {
    additionalDetails?: string
}

export type Charity = NonTrustDistribution & {
    charityBeneficiaries: CharityBeneficiary[]
}

export type CharityBeneficiary = {
    id?: string,
    charityId: string,
    name: string,
}

export type Outright = NonTrustDistribution & {
    outrightMembers?: Trustee[],
}

export type FlowchartModalInputs = {
    flowchartName?: string,
    estateItem: EstateItemType | undefined,
}

export type RevocableTrustModalInputs = FlowchartModalInputs & {
    revTrustType: Trustee[],
    assumedFirstDeath: string,
    distributionStructure: string,
    triggerEvent?: string
}

export type WillModalInputs = FlowchartModalInputs & {
    willOwner: string,
}

export type EstateMember = {
    id?: string,
    createdDate?: string,
    memberId?: string | null,
    memberOrder?: number,
    customName?: string,
    familyMember: boolean,
}

export type Trustee = EstateMember & {
    trustId: string | undefined,
    memberType: MemberType
}

export type FiduciaryMember = EstateMember & {
    fiduciaryAssignmentId?: string,
    fiduciaryMemberType: FiduciaryMemberType,
    memberOrderOverride?: number
}

export enum MemberType {
    Grantor = "GRANTOR",
    Trustee = "TRUSTEE",
    Successor = "SUCCESSOR",
    Beneficiary = "BENEFICIARY",
    FirstDeath = "FIRST_DEATH"
}

export enum FiduciaryMemberType {
    Executor = "EXECUTOR",
    Guardian = "GUARDIAN",
    FiduciaryTrustee = "FIDUCIARY_TRUSTEE",
    FinancialPowerOfAttorney = "FINANCIAL_POWER_OF_ATTORNEY",
    HealthcarePowerOfAttorney = "HEALTHCARE_POWER_OF_ATTORNEY"
}

export enum FiduciaryKey {
    Executors = "executors",
    GuardiansForMinors = "guardiansForMinors",
    Trustees = "trustees",
    FinancialPowerOfAttorneys = "financialPowerOfAttorneys",
    HealthcarePowerOfAttorneys = "healthcarePowerOfAttorneys"
}

export enum DistributionType {
    MARITAL = "MARITAL",
    CREDIT_SHELTER = "CREDIT_SHELTER",
    IN_TRUST = "IN_TRUST",
    SURVIVOR = "SURVIVOR",
    CHARITY = "CHARITY",
    OUTRIGHT = "OUTRIGHT",
    WILL = "WILL",
    MISCELLANEOUS = "MISCELLANEOUS",
    TRUST = "TRUST"
}

export type DrawerType = FlowchartEstateItemType | DistributionType;

// Flowchart visual types
export enum FlowchartEstateType {
    IN_ESTATE = "IN_ESTATE",
    OUT_OF_ESTATE = "OUT_OF_ESTATE",
    FULL_ESTATE = "FULL_ESTATE",
}


export enum FlowchartEstateItemType {
    REVOCABLE = "REVOCABLE",
    IRREVOCABLE = "IRREVOCABLE",
    WILL = "WILL",
    MISCELLANEOUS = "MISCELLANEOUS"
}

export type EstateItemVisualData = {
    estateItemType: FlowchartEstateType,
    firstLevelData: FirstLevelParentNodeProps,
    firstDeathBeneficiariesEdge?: FlowchartCustomEdgeProps,
    firstDeathBeneficiariesLevel?: DistributionsNodeProps,
    remainderBeneficiariesEdge?: FlowchartCustomEdgeProps,
    remainderBeneficiariesLevel?: DistributionsNodeProps,
    additionalDistributionEdge?: FlowchartCustomEdgeProps,
    additionalDistributionLevel?: DistributionsNodeProps,
}

export type FlowchartVisualDataAndDimensions = {
    nodes: Node[],
    edges: Edge[],
    flowchartHeight: number,
    flowchartWidth: number
}

export type Member = {
    value: string,
    label: string,
    selected: boolean
}

export type FamilyMembers = {
    value: string,
    label: string,
    familyMember: Member[]
}

export type State = {
    itemText: string;
    value: string;
}

export type DropdownItemType = {
    name: string;
    type: DistributionType;
}

export type FiduciarySummary = {
    primaryMember?: FiduciaryAssignment,
    partnerMember?: FiduciaryAssignment
}

export const emptyFiduciarySummary: FiduciarySummary = {
    primaryMember: {
        assignedToMemberId: "",
        executors: [],
        guardiansForMinors: [],
        trustees: [],
        financialPowerOfAttorneys: [],
        healthcarePowerOfAttorneys: []
    },
    partnerMember: undefined
}

export type FiduciaryAssignment = {
    fiduciaryAssignmentId?: string,
    assignedToMemberId: string,
    executors?: FiduciaryMember[],
    guardiansForMinors?: FiduciaryMember[],
    trustees?: FiduciaryMember[],
    financialPowerOfAttorneys?: FiduciaryMember[],
    healthcarePowerOfAttorneys?: FiduciaryMember[],
    continuedSections?: FiduciaryKey[]
}
